/**
 * Bare section wrapper shared by every marketing surface.
 *
 * What: Emits the `<section>` element the marketing pages share — the
 * `data-landing-section` hook the scroll-reveal and active-section systems key
 * on, the sticky-nav scroll margin, an optional tone wash, and the bottom
 * rhythm — and nothing else.
 * Why: The same wrapper was hand-rolled in every homepage and About section.
 * One primitive keeps section rhythm in a single place. `LandingSection`
 * composes this and adds a `Container`, for pages that don't supply their own.
 * When: Update when the marketing section rhythm, sticky-nav offset, or the
 * tone set changes.
 */

import type { ElementType, ReactNode } from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

import { colors } from '@/components/landing/landingTokens';

export type LandingSectionTone = 'transparent' | 'paper' | 'muted' | 'night';

const TONE_STYLES: Record<LandingSectionTone, Record<string, string>> = {
  transparent: {},
  paper: { backgroundColor: colors.paper, color: colors.ink },
  muted: { backgroundColor: colors.backgroundAlt, color: colors.ink },
  night: { backgroundColor: colors.night, color: colors.onNight },
};

/** Clearance for the sticky top bar when an anchor link lands on a section. */
const SECTION_SCROLL_MARGIN_TOP = { xs: 120, sm: 104, md: 96 };

/** Gap to the next section on the marketing rhythm. */
const SECTION_MB = { xs: 7, sm: 8, md: 9 };

interface LandingSectionShellProps {
  id?: string;
  as?: ElementType;
  ariaLabelledby?: string;
  ariaLabel?: string;
  tone?: LandingSectionTone;
  /** Pass `0` for full-bleed bands that stack flush against each other. */
  mb?: number | Record<string, number>;
  sx?: SxProps<Theme>;
  children: ReactNode;
}

function LandingSectionShell({
  id = undefined,
  as = 'section',
  ariaLabelledby = undefined,
  ariaLabel = undefined,
  tone = 'transparent',
  mb = SECTION_MB,
  sx = undefined,
  children,
}: LandingSectionShellProps) {
  return (
    <Box
      component={as}
      id={id}
      data-landing-section="true"
      aria-labelledby={ariaLabelledby}
      aria-label={ariaLabel}
      sx={{
        scrollMarginTop: SECTION_SCROLL_MARGIN_TOP,
        mb,
        ...TONE_STYLES[tone],
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export default LandingSectionShell;
